import { transition } from '@helpers/animate';
import brand from '@helpers/brand';
import { fluid } from '@helpers/fluid';
import fonts from '@helpers/fonts';
import { ButtonReset } from '@helpers/global';
import { Device, from, until } from '@helpers/media';
import srOnly from '@helpers/srOnly';
import { h4 } from '@helpers/typography';
import CardGrid from '@stories/Components/CardGrid/CardGrid';
import { cardWidth } from '@stories/Components/CardGrid/CardGrid.styles';
import TabbedSignpostCardStyles from '@stories/Components/Cards/TabbedSignpostCard/TabbedSignpostCard.styles';
import MotionTrackStyles from '@stories/Components/Misc/Carousels/Carousel/MotionTrack/MotionTrack.styles';
import SectionWrapper, { ContentType, } from '@stories/Components/Misc/SectionWrapper/SectionWrapper';
import styled from 'styled-components';
const Container = styled(SectionWrapper) `
  overflow: hidden;

  &[data-bg-color='beige'] {
    background: ${brand.global.beige};
    border-radius: 0 0 ${fluid(40, 95)};
  }
`;
const Wrapper = styled.div `
  display: grid;
`;
const TopArea = styled.div ``;
const Title = styled.h2 `
  ${h4};

  color: var(--sectionTitleColour);
  font-size: ${fluid(28, 38)};
  text-align: center;
  margin-bottom: ${fluid(26, 33)};
`;
const TabWrapper = styled.div `
  --buttonHeight: 35px;

  position: relative;
  margin: ${fluid(22, 33)} 0 ${fluid(22, 47)};
  display: flex;
  justify-content: center;

  &[data-hidden='true'] {
    ${srOnly};
  }
`;
const TabButton = styled.button `
  ${ButtonReset};
  ${transition('color, background, opacity, order, max-height, transform')};
  ${fonts.proximaNova.bold};

  --btnBgColor: ${brand.green.default};
  --btnFgColor: ${brand.global.white};

  background: transparent;

  &[data-type=${ContentType.Savings}] {
    --btnBgColor: ${brand.blue.default};
  }

  &[data-type=${ContentType.Intermediaries}] {
    --btnBgColor: ${brand.green.light};
    --btnFgColor: ${brand.blue.navy};
  }

  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${fluid(6, 10)};
  z-index: 1;
  padding: 3px ${fluid(16, 36)};
  color: ${brand.blue.dark};
  min-height: var(--buttonHeight);
  text-align: center;
  font-size: 16px;
  line-height: 1.25;
  border-radius: 100000000px;

  &:focus-visible {
    outline: auto;
    outline-offset: 3px;
    outline-width: 2px;
  }

  &:hover,
  &[aria-selected='true'] {
    background: var(--btnBgColor);
    color: var(--btnFgColor);

    &:focus-visible {
      outline-color: ${brand.global.black};
    }
  }

  &[aria-selected='false'] {
    &::before {
      max-width: 0px;
    }
  }

  svg {
    ${transition('transform', 0.25)};

    display: none;
    width: ${fluid(20, 26)};
  }
`;
const Tabs = styled.div `
  display: inline-flex;
  justify-content: center;
  gap: ${fluid(10, 27)};
  background-color: ${brand.global.beige};
  padding: 6px;
  border-radius: 1000000000px;

  ${Container}[data-background='beige'] & {
    background-color: ${brand.global.white};
  }

  @media ${until(Device.MobileLarge)} {
    min-width: 334px;

    &:not(:has(button:nth-of-type(3))) {
      margin: 0 calc(var(--sitePadding) * -1);

      ${TabButton} {
        flex: 1;
      }
    }
  }
`;
const SectionTab = styled.div `
  ${transition('opacity, transform, visibility', 1)};

  position: relative;
  grid-column: 1;
  grid-row: 2;
  overflow: hidden;

  @media ${from(Device.TabletLarge)} {
    overflow: visible;
  }

  &[data-active='false'] {
    opacity: 0;
    pointer-events: none;
    visibility: hidden;
  }
`;
const SixColGrid = styled(CardGrid) `
  --grow: 0;
  --rowItems: 2;
  --itemPeek: 24px;

  &:not(:has(${MotionTrackStyles.Track})) {
    ${TabbedSignpostCardStyles.Container} {
      ${cardWidth};
    }
  }

  @media ${from(Device.Tablet)} {
    --rowItems: 3 !important; // Important required to prevent CLS specificity issue
  }

  @media ${from(Device.TabletLarge)} {
    --rowItems: 4 !important;

    ${TabbedSignpostCardStyles.Container} {
      ${cardWidth};
    }
  }

  @media ${from(Device.Desktop)} {
    --rowItems: 6 !important;
  }
`;
const LinkArea = styled.div `
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: 11px;
  margin-top: ${fluid(30, 35)};

  @media ${until(Device.Tablet)} {
    flex-direction: column;
    align-items: center;
    gap: 23px;
  }
`;
const TabbedSignpoststyles = {
    Container,
    Wrapper,
    TopArea,
    Title,
    SixColGrid,
    LinkArea,
    TabWrapper,
    TabButton,
    Tabs,
    SectionTab,
};
export default TabbedSignpoststyles;
